import { TextField } from '@cxc/react-fds-components';
import React from 'react';
import { Control, Controller, FieldValues } from 'react-hook-form';
import { AemTextField } from '../../constants/types/AemTypes';
import {
  RequiredErrorMessage,
  Validator,
  generateRules,
} from './generateRules';

export type ControlledTextFieldProps<T extends FieldValues> = {
  control: Control<T, unknown>;
  field: AemTextField;
  validator?: Validator;
  additionalOnChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  forceUppercase?: boolean;
} & React.ComponentProps<typeof TextField>;

export const ControlledTextField = <T extends FieldValues>({
  control,
  field,
  validator = undefined,
  additionalOnChange = undefined,
  ...textFieldProps
}: ControlledTextFieldProps<T>) => {
  const { id, label, maxLength } = field;

  const rules = generateRules(field, validator);

  const fieldIsEnabled = !textFieldProps.disabled;
  const validationProps = fieldIsEnabled
    ? {
        validationMode: 'all',
      }
    : {};
  const required = (rules.required as RequiredErrorMessage)?.value;

  return (
    <Controller
      name={id}
      control={control as Control}
      rules={rules}
      defaultValue={''}
      render={({
        field: { onChange, onBlur, value },
        fieldState: { error },
      }) => (
        <TextField
          id={id}
          name={label}
          value={value as string | undefined}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            onChange(e);
            if (additionalOnChange) {
              additionalOnChange(e);
            }
          }}
          onBlur={onBlur}
          label={required ? `${label} *` : label}
          message={error?.message}
          error={error}
          maxLength={maxLength}
          required={!!required}
          {...validationProps}
          {...textFieldProps}
        />
      )}
    />
  );
};
